import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, Trash2, Users, User as UserIcon, FolderKanban, ListChecks, Link as LinkIcon } from 'lucide-react';
import api from '@host/axios';
import toast from 'react-hot-toast';
import { sid } from '../../utils/shortId';
import { useConfirm } from '../common/ConfirmDialog';

import { Trans, useLingui } from '@lingui/react/macro';

import { msg } from "@lingui/core/macro";
import { tLabel } from "../../i18n/runtime.js";

/**
 * ShareResourceModal — Asana-style sharing cho Project & TaskList.
 * Props:
 *   resource: { type: 'project' | 'list', id, name }
 *   onClose: () => void
 *   onChanged?: () => void
 *
 * Endpoints:
 *   project: GET/POST /tasks/projects/:id/members, PUT/DELETE /tasks/projects/:id/members/:memberId
 *   list:    GET/POST /tasks/lists/:id/members,    PUT/DELETE /tasks/lists/:id/members/:memberId
 */

const ROLES = [
  { key: 'ADMIN',       label: msg`Quản trị`,  desc: msg`Toàn quyền — có thể chia sẻ, xoá, đổi role` },
  { key: 'EDITOR',      label: msg`Chỉnh sửa`, desc: msg`Tạo/sửa/xoá task & list` },
  { key: 'CONTRIBUTOR', label: msg`Đóng góp`,  desc: msg`Tạo task của riêng mình` },
  { key: 'COMMENTER',   label: msg`Bình luận`, desc: msg`Chỉ comment, không sửa` },
  { key: 'VIEWER',      label: msg({ message: 'Xem', context: 'vai trò chia sẻ' }), desc: msg`Chỉ xem` },
];
// ⚠️ `.label` là MessageDescriptor (`msg`…``) — bản production strip `message`, chỉ còn
// `{id:"szmtzT"}`. Render thẳng object đó là React error #31 (đã sập nút Chia sẻ dự án/list).
// tLabel() dịch descriptor, chuỗi thường trả nguyên → nuốt được cả 2 dạng trong ROLES.
const roleLabel = (r) => tLabel(ROLES.find(x => x.key === r)?.label) || r;

function basePath(type, id) {
  return type === 'project' ? `/tasks/projects/${id}/members` : `/tasks/lists/${id}/members`;
}

// BE chuẩn trả { owner|creator, members[], my_role }. BE cũ (SaaS metadata.members) trả MẢNG thuần
// → `r.data.data || {members:[]}` giữ nguyên mảng (truthy) → data.members undefined →
// `data.members.length` ném TypeError → ErrorBoundary nuốt trắng cả trang /tasks
// (GlitchTip issue #17 + #18). Normalize về đúng shape, mọi payload lạ đều degrade an toàn.
function normalizeShareData(payload) {
  if (!payload || Array.isArray(payload)) return { owner: null, creator: null, members: [], my_role: null };
  return {
    owner: payload.owner || null,
    creator: payload.creator || null,
    members: Array.isArray(payload.members) ? payload.members : [],
    my_role: payload.my_role || null,
  };
}

export default function ShareResourceModal({ resource, onClose, onChanged }) {
  const {
    t
  } = useLingui();

  const { type, id, name } = resource;
  const [confirmDialog, ConfirmUI] = useConfirm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ owner: null, creator: null, members: [], my_role: null });
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);
  const fadeOut = (cb) => { setVisible(false); setTimeout(cb, 180); };
  const handleClose = () => fadeOut(onClose);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(basePath(type, id));
      setData(normalizeShareData(r.data?.data));
    } catch (e) {
      toast.error(e.response?.data?.message || t`Không tải được danh sách thành viên`);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [type, id]);

  const ownerOrCreator = data.owner || data.creator;
  const myRole = data.my_role;
  const canManage = myRole === 'ADMIN';

  const updateRole = async (member, role) => {
    setBusy(true);
    try {
      await api.put(`${basePath(type, id)}/${member.id}`, { role });
      toast.success(t`Đã cập nhật`);
      onChanged?.();
      reload();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
    finally { setBusy(false); }
  };

  const removeMember = async (member) => {
    const memberName = member.target?.name || t`thành viên`;
    const ok = await confirmDialog({
      title: t`Xoá thành viên`,
      message: type === 'project' ? t`Bỏ ${memberName} khỏi dự án?` : t`Bỏ ${memberName} khỏi list?`,
      variant: 'danger', confirmText: t`Xoá`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`${basePath(type, id)}/${member.id}`);
      toast.success(t`Đã xoá`);
      onChanged?.();
      reload();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
    finally { setBusy(false); }
  };

  const addEntity = async ({ target_type, target_id, role }) => {
    setBusy(true);
    try {
      await api.post(basePath(type, id), { target_type, target_id, role });
      toast.success(t`Đã thêm`);
      setPickerOpen(false);
      onChanged?.();
      reload();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi thêm`); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    const url = type === 'project'
      ? `${window.location.origin}/tasks?project=${sid(id)}`
      : `${window.location.origin}/tasks?list=${sid(id)}`;
    navigator.clipboard?.writeText(url)
      .then(() => toast.success(t`Đã copy link`))
      .catch(() => toast.error(t`Không copy được`));
  };

  const Icon = type === 'project' ? FolderKanban : ListChecks;
  const titleText = type === 'project' ? t`Chia sẻ dự án "${name}"` : t`Chia sẻ list "${name}"`;

  // ⚠️ PHẢI portal ra body: modal này mở từ menu "..." của dự án TRONG NAV PHỤ
  // (TasksSecondaryNav.jsx), mà lớp phủ v2 xoá nền mọi `div[class*='bg-white']` nằm trong
  // nav phụ 240px (cnv-ui/src/styles/ui-v2.css) → render inline là tấm modal mất nền,
  // nhìn xuyên. Nav phụ còn nằm trong <aside class="fixed ... z-50"> nên z-[10000] bị kẹp
  // trong tầng 50. Mở từ TasksPage thì không sao — nhưng cùng một component, portal luôn.
  return createPortal(
    <>
      {ConfirmUI}
      <div className={`fixed inset-0 z-[10000] flex items-center justify-center transition-opacity duration-200 ${visible ? 'bg-black/40' : 'bg-black/0'}`}
        onClick={handleClose}>
        <div className={`bg-white rounded-2xl shadow-xl w-[600px] max-w-[95vw] max-h-[85vh] flex flex-col transform transition-all duration-200 ease-out ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <Icon size={18} className="text-blue-600 shrink-0" />
              <h3 className="text-lg font-bold text-gray-900 truncate">{titleText}</h3>
            </div>
            <button onClick={handleClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition"><X size={16} /></button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={20} className="text-gray-400 animate-spin" /></div>
            ) : (
              <>
                {/* Add picker */}
                {canManage && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2"><Trans>Thêm người hoặc phòng ban</Trans></label>
                    {pickerOpen ? (
                      <AddPicker existingTargets={data.members} ownerId={ownerOrCreator?.id}
                        onCancel={() => setPickerOpen(false)}
                        onAdd={addEntity} busy={busy} />
                    ) : (
                      <button onClick={() => setPickerOpen(true)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 rounded-xl text-sm text-gray-600 hover:border-blue-400 hover:bg-blue-50/40 transition">
                        <Search size={14} /> <Trans>Tìm và thêm thành viên...</Trans>
                      </button>
                    )}
                  </div>
                )}

                {/* Members list */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Trans>Đang có quyền (</Trans>{(ownerOrCreator ? 1 : 0) + data.members.length})
                                      </label>
                  <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                    {/* Owner row */}
                    {ownerOrCreator && (
                      <Row entity={ownerOrCreator} role="OWNER" isOwner readOnly />
                    )}
                    {data.members.map(m => (
                      <Row key={m.id} entity={m.target} role={m.role}
                        targetType={m.target_type}
                        readOnly={!canManage}
                        onRoleChange={(r) => updateRole(m, r)}
                        onRemove={() => removeMember(m)}
                        busy={busy} />
                    ))}
                    {data.members.length === 0 && (
                      <p className="text-xs text-gray-400 px-3 py-3 text-center"><Trans>Chưa có thành viên nào được chia sẻ</Trans></p>
                    )}
                  </div>
                  {!canManage && myRole && (
                    <p className="text-[11px] text-gray-400 mt-2 px-1">
                      <Trans>Bạn có quyền</Trans> <span className="font-medium">{roleLabel(myRole)}</span> <Trans>— chỉ Quản trị mới có thể chia sẻ.</Trans>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 shrink-0 bg-gray-50/40">
            <p className="text-xs text-gray-500">
              {type === 'project' ? t`Quyền dự án áp dụng cho mọi list/task con` : t`List inherit role từ project (lấy max)`}
            </p>
            <button onClick={copyLink}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition">
              <LinkIcon size={13} /> Copy link
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ── Row: hiển thị 1 entity (user/team) + role dropdown ── */
function Row({ entity, role, targetType, isOwner, readOnly, onRoleChange, onRemove, busy }) {
  const {
    t
  } = useLingui();

  if (!entity) return null;
  const isTeam = targetType === 'team';

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 group">
      {isTeam ? (
        <div className="w-9 h-9 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center shrink-0">
          <Users size={15} className="text-purple-600" />
        </div>
      ) : entity.avatar ? (
        <img src={entity.avatar} alt={entity.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
          {entity.name?.[0] || '?'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">
          {entity.name}
          {isTeam && entity.code && <span className="ml-1.5 text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{entity.code}</span>}
        </p>
        <p className="text-[11px] text-gray-400 truncate">
          {isTeam ? t`Phòng ban — áp dụng cho mọi thành viên` : (entity.position || entity.email || '')}
        </p>
      </div>
      {isOwner ? (
        <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded"><Trans>Chủ sở hữu</Trans></span>
      ) : readOnly ? (
        <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-1 rounded">{roleLabel(role)}</span>
      ) : (
        <RoleSelect value={role} onChange={onRoleChange} disabled={busy} />
      )}
      {!isOwner && !readOnly && (
        <button onClick={onRemove} disabled={busy}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition disabled:opacity-30"
          title={t`Xoá`}>
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function RoleSelect({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const cur = ROLES.find(r => r.key === value);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled}
        className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded hover:bg-blue-100 disabled:opacity-50">
        {tLabel(cur?.label) || value}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50">
          {ROLES.map(r => (
            <button key={r.key} type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(r.key); setOpen(false); }}
              className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${r.key === value ? 'bg-blue-50' : ''}`}>
              <p className="text-sm font-medium text-gray-900">{tLabel(r.label)}</p>
              <p className="text-[10px] text-gray-500">{tLabel(r.desc)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── AddPicker: search user / team rồi chọn role và Add ── */
function AddPicker({ existingTargets, ownerId, onCancel, onAdd, busy }) {
  const {
    t
  } = useLingui();

  const [tab, setTab] = useState('user'); // 'user' | 'team'
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null); // { type, id, name }
  const [role, setRole] = useState('EDITOR');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/users/simple', { params: { is_active: true, limit: 50, search: search || undefined } }).then(r => r.data?.data || []).catch(() => []),
      api.get('/teams', { params: { limit: 100 } }).then(r => r.data?.data || []).catch(() => []),
    ]).then(([us, ts]) => { setUsers(us); setTeams(ts); }).finally(() => setLoading(false));
  }, [search]);

  const existingUserIds = useMemo(() => new Set(existingTargets.filter(m => m.target_type === 'user').map(m => m.target_id)), [existingTargets]);
  const existingTeamIds = useMemo(() => new Set(existingTargets.filter(m => m.target_type === 'team').map(m => m.target_id)), [existingTargets]);

  const userResults = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u =>
      u.id !== ownerId &&
      !existingUserIds.has(u.id) &&
      (!q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
    ).slice(0, 30);
  }, [users, search, existingUserIds, ownerId]);

  const teamResults = useMemo(() => {
    const q = search.toLowerCase();
    return teams.filter(t => !existingTeamIds.has(t.id) && (!q || t.name?.toLowerCase().includes(q) || t.code?.toLowerCase().includes(q))).slice(0, 30);
  }, [teams, search, existingTeamIds]);

  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/30 space-y-3">
      <div className="flex items-center gap-1 bg-white rounded-lg p-0.5 border border-gray-200 w-fit">
        {[{ k: 'user', l: t`Nhân viên`, icon: UserIcon }, { k: 'team', l: t`Phòng ban`, icon: Users }].map(opt => (
          <button key={opt.k} onClick={() => { setTab(opt.k); setPicked(null); }}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition ${tab === opt.k ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <opt.icon size={12} /> {opt.l}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
          placeholder={tab === 'user' ? t`Nhập tên hoặc email...` : t`Nhập tên phòng ban...`}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none" />
      </div>

      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white">
        {loading && <div className="flex justify-center py-3"><Loader2 size={14} className="text-gray-400 animate-spin" /></div>}
        {!loading && tab === 'user' && (
          userResults.length === 0
            ? <p className="text-xs text-gray-400 px-3 py-3 text-center"><Trans>Không có ai khớp</Trans></p>
            : userResults.map(u => (
              <button key={u.id}
                onClick={() => setPicked({ type: 'user', id: u.id, name: u.name })}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 transition ${picked?.id === u.id ? 'bg-blue-50' : ''}`}>
                {u.avatar ? <img src={u.avatar} className="w-7 h-7 rounded-full object-cover" />
                  : <div className="w-7 h-7 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{u.name?.[0] || '?'}</div>}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{u.name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{u.email || u.position || ''}</p>
                </div>
              </button>
            ))
        )}
        {!loading && tab === 'team' && (
          teamResults.length === 0
            ? <p className="text-xs text-gray-400 px-3 py-3 text-center"><Trans>Không có phòng ban khớp</Trans></p>
            : teamResults.map(t => (
              <button key={t.id}
                onClick={() => setPicked({ type: 'team', id: t.id, name: t.name })}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-purple-50 transition ${picked?.id === t.id ? 'bg-purple-50' : ''}`}>
                <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center"><Users size={12} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{t.name}</p>
                  {t.code && <p className="text-[10px] text-gray-400 truncate">{t.code}</p>}
                </div>
              </button>
            ))
        )}
      </div>

      {/* Role + actions */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="text-xs text-gray-500 flex-1 truncate">
          {picked ? <><Trans>Đã chọn:</Trans> <span className="font-semibold text-gray-800">{picked.name}</span></> : t`Chọn 1 mục để thêm`}
        </div>
        <select value={role} onChange={e => setRole(e.target.value)}
          className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none">
          {ROLES.map(r => <option key={r.key} value={r.key}>{tLabel(r.label)}</option>)}
        </select>
        <button onClick={onCancel} disabled={busy}
          className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-white"><Trans>Huỷ</Trans></button>
        <button onClick={() => picked && onAdd({ target_type: picked.type, target_id: picked.id, role })}
          disabled={!picked || busy}
          className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40">
          <Trans>Thêm</Trans>
        </button>
      </div>
    </div>
  );
}
